/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { Button } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { SidebarDropdown } from "~/components/SidebarForm/inputs/SidebarDropdown";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import { SidebarFormButtons } from "~/components/SidebarForm/SidebarFormButtons";
import { showSuccess } from "~/components/Toast";
import { useAPIData } from "~/hooks/useAPIData";
import { ProductListItem, RouteParams } from "~/models";
import { ACTIONS, Can } from "~/routing";
import { generateActionSuccessText } from "~/tools";
import { useAxios } from "~/tools/hooks/useAxios";
import { AssignedProduct, AssignedProductItem } from "./types";

type Values = {
  product: string;
};
type ProductAddProps = {
  assignedProducts: AssignedProduct[];
  refetch: () => void;
};

function AssignProduct({ assignedProducts, refetch }: ProductAddProps) {
  const state = useParams<RouteParams>();
  const { customerId } = state;

  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const { showError } = useShowError();
  const { Axios } = useAxios();

  const methods = useForm();

  const [products] = useAPIData<ProductListItem[]>(
    "/search/products",
    (resp) => resp.searchResults,
    []
  );

  const onSubmit = async (values: Values) => {
    setIsLoading(true);
    const organizeValues = { productId: values.product };
    try {
      await Axios().post(`/customers/${customerId}/products`, organizeValues);
      showSuccess(generateActionSuccessText(`Product`)()(`added`)());
      refetch();
    } catch (e) {
      showError(e.response);
    }
    setIsLoading(false);
    setIsSidebarOpen(false);
  };

  const flattAssignedProducts = assignedProducts
    .map((p: AssignedProduct) => p.product)
    .map((i: AssignedProductItem) => i.id);

  const filteredProducts =
    products.filter(({ id }) => !flattAssignedProducts?.includes(id)) || [];

  const productOptions =
    filteredProducts?.map((product) => ({
      value: product.id,
      label: product.name,
    })) || [];

  function AssignProductSidebar() {
    return (
      <SidebarForm
        title="Assign Product"
        open={isSidebarOpen}
        setOpen={setIsSidebarOpen}
        formMethods={methods}
        submit={onSubmit}
      >
        <SidebarDropdown
          name="product"
          options={productOptions}
          label="Product"
          required
        />

        <SidebarFormButtons
          onCancel={() => setIsSidebarOpen(false)}
          submitLoading={isLoading}
          submitEnabled={methods.watch("product")}
        />
      </SidebarForm>
    );
  }

  return (
    <>
      <Can action={ACTIONS.ADD_CUSTOMER_PRODUCT}>
        <Button
          onClick={() => setIsSidebarOpen(true)}
          css={{ marginLeft: "auto" }}
          icon="plus"
        >
          Assign Product
        </Button>
      </Can>

      {AssignProductSidebar()}
    </>
  );
}

export { AssignProduct };
